import React, { useEffect, useMemo, useState } from "react";
import { Input } from "antd";
import { Icon } from "Components";
import InfinityScrollWrapper from "Components/InfinityScroll/InfinityScrollWrapper";
import PropertySelect from "../CampaignSteps/components/PropertySelect";
import RecipientItem from "../CampaignSteps/components/RecipientItem";

interface GroupOption {
    key: string;
    label: string;
    count: number;
    propertyKey: string;
}

interface PropertyOption {
    value: string;
    label: string;
    count?: number;
}

interface Props {
    propertyOptions: PropertyOption[];
    groupOptions: GroupOption[];
    selectedGroups: string[];
    setSelectedGroups: (groups: string[]) => void;
    groupSearchQuery: string;
    setGroupSearchQuery: (value: string) => void;
}

const GroupRecipients: React.FC<Props> = ({
    propertyOptions,
    groupOptions,
    selectedGroups,
    setSelectedGroups,
    groupSearchQuery,
    setGroupSearchQuery,
}) => {
    const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
    const [allSelected, setAllSelected] = useState(false);
    const [allGroupsSelected, setAllGroupsSelected] = useState(false);

    const filteredGroups = useMemo(() => {
        return groupOptions.filter(
            (g) =>
                (allSelected || selectedProperties.includes(g.propertyKey)) &&
                g.label.toLowerCase().includes(groupSearchQuery.toLowerCase())
        );
    }, [groupOptions, selectedProperties, allSelected, groupSearchQuery]);

    const totalUsersCount = filteredGroups
        .filter((r) => selectedGroups.includes(r.key))
        .reduce((acc, r) => acc + r.count, 0);

    useEffect((() => {
        if (!!allGroupsSelected) {
            setSelectedGroups(filteredGroups.map((ind) => ind.key));
        } else {
            setSelectedGroups([]);
        }
    }), [allGroupsSelected]);

    useEffect((() => {
        if (!allSelected) {
            setSelectedGroups([]);
        }
    }), [allSelected]);

    const handleSelectAllGroups = () => {
        setAllGroupsSelected(!allGroupsSelected);
    };

    const handleGroupToggle = (key: string) => {
        if (selectedGroups.includes(key)) {
            setSelectedGroups(selectedGroups.filter((g) => g !== key));
        } else {
            setSelectedGroups([...selectedGroups, key]);
        }
    };

    return (
        <div className="phishing-step3__role-section">
            <div className="phishing-step3__role-filters">
                <div className="phishing-step3__filter-group">
                    <div className="phishing-step3__label">
                        Select property <span className="required">*</span>
                    </div>
                    <PropertySelect
                        propertyOptions={propertyOptions}
                        selectedProperties={selectedProperties}
                        setSelectedProperties={setSelectedProperties}
                        allSelected={allSelected}
                        setAllSelected={setAllSelected}
                        onPropertyDeselect={(propertyValue) => {
                            setSelectedGroups(
                                selectedGroups.filter((groupId) => {
                                    const group = groupOptions.find((g) => g.key === groupId);
                                    return group?.propertyKey !== propertyValue;
                                })
                            );
                        }}
                    />
                </div>

                <div className="phishing-step3__filter-group phishing-step3__role-search">
                    <div>
                        <Input.Search
                            placeholder="Search groups"
                            value={groupSearchQuery}
                            onChange={(e: any) => setGroupSearchQuery(e.target.value)}
                            suffix={<Icon type="search" />}
                        />
                    </div>
                </div>
            </div>

            <div className="phishing-step3__role-list-header">
                <div className="phishing-step3__label">
                    Select a group
                    {filteredGroups.length > 0 && (
                        <>
                            <span className="phishing-step3__dot"> </span>
                            <span className="phishing-step3__total-count">
                                ({filteredGroups.length} Results)
                            </span>
                        </>
                    )}
                    {totalUsersCount > 0 && (
                        <>
                            <span className="phishing-step3__dot"> · </span>
                            <span className="phishing-step3__total-count">
                                {totalUsersCount} users selected 
                            </span>
                        </>
                    )} 
                    <span className="required"> * </span>
                </div>
                <button
                    className="phishing-step3__select-all"
                    onClick={handleSelectAllGroups}
                >
                    {((filteredGroups.length > 0 &&
                        filteredGroups.every((ind) => selectedGroups.includes(ind.key)))) ? "Deselect all" : "Select all"}
                </button>
            </div>

            <div className="phishing-step3__role-list">
                {selectedProperties.length === 0 && !allSelected ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        Please select a property to see groups.
                    </div>
                ) : filteredGroups.length === 0 ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        No groups found.
                    </div>
                ) : (
                    <InfinityScrollWrapper
                        dataLength={filteredGroups.length}
                        hasMore={false}
                        endMessageText=""
                        onLoadMore={() => { console.log("Load more.."); }}
                        loading={false}
                        height={400}
                        scrollableId="group-list-infinite-scroll"
                        loaderText="Loading groups..."
                    >
                        {filteredGroups.map((group) => (
                            <RecipientItem
                                key={group.key}
                                itemKey={group.key}
                                title={group.label}
                                description={`${group.count} users`}
                                iconType="recipientGroup"
                                isChecked={selectedGroups.includes(group.key)}
                                onToggle={handleGroupToggle}
                            />
                        ))}
                    </InfinityScrollWrapper>
                )}
            </div>
        </div>
    );
};

export default GroupRecipients;